import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { environment } from '../../../environments/environment';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import { CheckInRequest } from '../../interfaces/checkin-request';
import { CheckInResponse } from '../../interfaces/checkin-response';
import { BudgetsToAdd } from '../../interfaces/budgets-to-add';
import { AddProceduresToCheckInRequest } from '../../interfaces/add-procedures-to-check-in-request';
import { FinishExamCheckInRequest } from '../../interfaces/finish-exam-checkin-request';
import { ExamGuideFile } from '../../interfaces/exam-guide-file';
import { EditScheduledProcedure } from '../../interfaces/edit-scheduled-procedure';
import { AvailableProcedure } from '../../interfaces/available-procedure';

@Injectable({
  providedIn: 'root',
})
export class CheckinService {
  protected apiVersion = environment.apiVersion;
  protected apiURL = environment.apiUrl;

  constructor(
    protected httpClient: HttpClient,
    private localStorageService: LocalStorageService,
  ) {
    this.apiURL = this.apiURL + '/v' + this.apiVersion;
  }

  public async openAttendance(
    checkInRequest: CheckInRequest,
  ): Promise<CheckInResponse> {
    const response = await firstValueFrom(
      this.httpClient
        .post<any>(
          `${this.apiURL}/CheckIn/check-in-appointment`,
          checkInRequest,
        )
        .pipe(catchError(this.handleError)),
    );

    if (!response?.data?.length) {
      throw new Error('Atendimento não retornado pela API.');
    }

    return response.data[0];
  }

  public async finishExamCheckIn(
    checkInRequest: FinishExamCheckInRequest,
  ): Promise<CheckInResponse> {
    const response = await firstValueFrom(
      this.httpClient
        .post<any>(
          `${this.apiURL}/CheckIn/finish-exam-check-in`,
          checkInRequest,
        )
        .pipe(catchError(this.handleError)),
    );

    return response.data[0];
  }

  public async getAllBudgetsIds(clientId: number): Promise<number[]> {
    const response: any = await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/CheckIn/get-budgets-ids/${clientId}`)
        .pipe(catchError(this.handleError)),
    );
    return response.data && response.data.length > 0 ? response.data[0] : [];
  }

  public async getProceduresFromBudget(
    budgetId: number,
  ): Promise<BudgetsToAdd[]> {
    const response = await firstValueFrom(
      this.httpClient
        .get<any>(
          `${this.apiURL}/CheckIn/get-procedures-from-budget/${budgetId}`,
        )
        .pipe(catchError(this.handleError)),
    );
    return response.data[0];
  }

  public async getAvailableProcedures(): Promise<AvailableProcedure[]> {
    const response = await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/CheckIn/get-available-procedures`)
        .pipe(catchError(this.handleError)),
    );
    return response.data[0];
  }

  public async addProceduresToCheckIn(
    request: AddProceduresToCheckInRequest,
  ): Promise<any> {
    return await firstValueFrom(
      this.httpClient
        .post(`${this.apiURL}/CheckIn/add-procedures-to-checkin`, request)
        .pipe(catchError(this.handleError)),
    );
  }

  public async updateScheduledProcedureExecutor(
    update: EditScheduledProcedure,
  ): Promise<void> {
    await firstValueFrom(
      this.httpClient
        .put(`${this.apiURL}/CheckIn/update-scheduled-executor`, update)
        .pipe(catchError(this.handleError)),
    );
  }

  public async getOwedAmount(sentBudgetId: number): Promise<number> {
    const response = await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/CheckIn/get-owed-amount/${sentBudgetId}`)
        .pipe(catchError(this.handleError)),
    );
    return response.data[0];
  }

  public async downloadExamGuide(checkInId: number): Promise<ExamGuideFile[]> {
    return await firstValueFrom(
      this.httpClient
        .get<
          ExamGuideFile[]
        >(`${this.apiURL}/CheckIn/download-exam-guide/${checkInId}`)
        .pipe(catchError(this.handleError)),
    );
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Erro inesperado. Tente novamente.';

    if (error?.error?.notifications?.length) {
      errorMessage = error.error.notifications[0].message;
    }

    if (errorMessage.includes('Atendimento sem senha vinculada')) {
      errorMessage =
        'Atendimento sem senha vinculada. Vincule uma senha ou faça uma nova chamada e tente novamente.';
    }

    console.error('HTTP Error:', error);

    return throwError(() => new Error(errorMessage));
  }
}
